import { useState, useEffect, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const ah = () => ({ Authorization: "Bearer " + localStorage.getItem("nexo_token"), "Content-Type": "application/json" });

function getWeekKey(userId) {
  const d = new Date();
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
  return `km_lock_${userId}_${d.getFullYear()}_W${week}`;
}

const EST = { installation: 240, maintenance: 120, depannage: 90, diagnostic: 60, "mise en service": 180, sav: 90 };
const getEst = (i) => {
  const t = (i.type_intervention || "").toLowerCase();
  return Object.entries(EST).find(([k]) => t.includes(k))?.[1] || 120;
};

const SC = { planifiee: "#f5a623", en_cours: "#5b6ef8", terminee: "#22d3a0", annulee: "#ef4444" };
const SL = { planifiee: "Planifié", en_cours: "En cours", terminee: "Terminé", annulee: "Annulé" };

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDay = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) : "—";
const fmtFull = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const isExpired = (d) => d && new Date(d) < new Date();
const isExpiringSoon = (d) => { if (!d) return false; const diff = new Date(d) - new Date(); return diff > 0 && diff < 30 * 86400000; };
const fmtChrono = (sec) => {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function PortailTechnicien({ T, user, onOpenPV }) {
  const [tab, setTab] = useState("planning");
  const [interventions, setInterventions] = useState([]);
  const [todos, setTodos] = useState([]);
  const [vehicule, setVehicule] = useState(null);
  const [stockTampon, setStockTampon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoStatus, setGeoStatus] = useState("idle");
  const [chronoSec, setChronoSec] = useState(0);
  const [newTodoText, setNewTodoText] = useState("");
  const [useStockItem, setUseStockItem] = useState(null);
  const [useQty, setUseQty] = useState(1);
  const [kmValue, setKmValue] = useState("");
  const [kmSaving, setKmSaving] = useState(false);
  const [kmError, setKmError] = useState("");
  const [startingId, setStartingId] = useState(null);
  const [echecModal, setEchecModal] = useState(null); // intervention en cours d'échec
  const [echecMotif, setEchecMotif] = useState("");
  const [echecSaving, setEchecSaving] = useState(false);

  // Monday km lock
  const isMonday = new Date().getDay() === 1;
  const kmKey = getWeekKey(user.id);
  const [kmLocked, setKmLocked] = useState(false); // resolved after vehicule loads

  const loadAll = useCallback(async () => {
    const [ivRes, todosRes, vehsRes, stockRes] = await Promise.all([
      fetch(`${BASE}/interventions`, { headers: ah() }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/todos`, { headers: ah() }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/vehicules`, { headers: ah() }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/stock-technicien/me`, { headers: ah() }).then(r => r.json()).catch(() => []),
    ]);

    const myInts = (Array.isArray(ivRes) ? ivRes : []).filter(i =>
      i.technicien_id === user.id || i.technicien_nom === (user.prenom + " " + user.nom)
    );
    setInterventions(myInts);
    setTodos(Array.isArray(todosRes) ? todosRes : []);

    const myVeh = (Array.isArray(vehsRes) ? vehsRes : []).find(v => v.technicien_id === user.id) || null;
    setVehicule(myVeh);
    setStockTampon(Array.isArray(stockRes) ? stockRes : []);
    setLoading(false);

    // Evaluate km lock after vehicle loads
    if (isMonday && !localStorage.getItem(kmKey) && myVeh) {
      setKmLocked(true);
    }
  }, [user.id, user.prenom, user.nom, isMonday, kmKey]);

  useEffect(() => {
    loadAll();
    const t1 = setInterval(loadAll, 60000);
    const t2 = setInterval(() => setChronoSec(s => s + 1), 1000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [loadAll]);

  // Geolocation
  useEffect(() => {
    if (!navigator.geolocation) return;
    let lastSent = 0;
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        setGeoStatus("tracking");
        const now = Date.now();
        if (now - lastSent < 25000) return;
        lastSent = now;
        fetch(`${BASE}/users/${user.id}/location`, {
          method: "PATCH", headers: ah(),
          body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        }).catch(() => {});
      },
      () => setGeoStatus("denied"),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [user.id]);

  const submitKm = async () => {
    const km = parseInt(kmValue);
    if (!kmValue || isNaN(km) || km <= 0) { setKmError("Veuillez entrer un kilométrage valide"); return; }
    if (!vehicule) { setKmError("Aucun véhicule assigné."); return; }
    setKmSaving(true); setKmError("");
    try {
      const r = await fetch(`${BASE}/vehicules/${vehicule.id}/releve`, {
        method: "POST", headers: ah(),
        body: JSON.stringify({ kilometrage: km }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      localStorage.setItem(kmKey, kmValue);
      setKmLocked(false);
      setKmValue("");
      loadAll();
    } catch (e) {
      setKmError("Erreur : " + e.message);
    } finally {
      setKmSaving(false);
    }
  };

  const useStock = async () => {
    if (!useStockItem) return;
    await fetch(`${BASE}/stock-technicien/mouvement`, {
      method: "POST", headers: ah(),
      body: JSON.stringify({ technicien_id: user.id, produit_id: useStockItem.produit_id, type: "sortie", quantite: useQty, motif: "Utilisation intervention" }),
    }).catch(() => {});
    setUseStockItem(null); setUseQty(1);
    loadAll();
  };

  const addTodo = async () => {
    if (!newTodoText.trim()) return;
    await fetch(`${BASE}/todos`, { method: "POST", headers: ah(), body: JSON.stringify({ titre: newTodoText.trim(), statut: "todo" }) }).catch(() => {});
    setNewTodoText(""); loadAll();
  };

  const completeTodo = async (id) => {
    await fetch(`${BASE}/todos/${id}`, { method: "PATCH", headers: ah(), body: JSON.stringify({ statut: "done" }) }).catch(() => {});
    loadAll();
  };

  const startIntervention = async (intervention) => {
    setStartingId(intervention.id);
    try {
      await fetch(`${BASE}/interventions/${intervention.id}/start`, { method: "POST", headers: ah() });
      await loadAll();
    } catch(e) { /* ignore */ } finally {
      setStartingId(null);
    }
    onOpenPV && onOpenPV(intervention);
  };

  const signalerEchec = async () => {
    if (!echecModal) return;
    setEchecSaving(true);
    try {
      await fetch(`${BASE}/interventions/${echecModal.id}/echec`, {
        method: "POST", headers: ah(),
        body: JSON.stringify({ motif: echecMotif.trim() || "Intervention non résolue" }),
      });
      await loadAll();
      setEchecModal(null);
      setEchecMotif("");
    } catch(e) { /* ignore */ } finally {
      setEchecSaving(false);
    }
  };

  // Derived data
  const todayStr = new Date().toDateString();
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const todayInts = interventions
    .filter(i => {
      const d = new Date(i.date_planifiee || i.created_at);
      return d.toDateString() === todayStr && !["terminee", "completed"].includes(i.statut);
    })
    .sort((a, b) => new Date(a.date_planifiee || 0) - new Date(b.date_planifiee || 0));

  const upcomingInts = interventions
    .filter(i => {
      const d = new Date(i.date_planifiee || i.created_at);
      return d.toDateString() !== todayStr && !["terminee", "completed", "annulee"].includes(i.statut);
    })
    .sort((a, b) => new Date(a.date_planifiee || 0) - new Date(b.date_planifiee || 0))
    .slice(0, 4);

  const activeInt = interventions.find(i => ["en_cours", "in-progress"].includes(i.statut));
  const totalDone = interventions.filter(i => ["terminee", "completed"].includes(i.statut)).length;
  const thisMonthDone = interventions.filter(i => ["terminee", "completed"].includes(i.statut) && new Date(i.date_planifiee || i.created_at) >= monthStart).length;
  const tauxPP = interventions.length > 0 ? Math.round(totalDone / interventions.length * 100) : 0;
  const cumulatedHours = Math.round(interventions.filter(i => ["terminee", "completed"].includes(i.statut)).reduce((s, i) => s + getEst(i), 0) / 60);
  const activeTodos = todos.filter(t => t.statut !== "done");

  const TABS = [
    { id: "planning", lbl: "📅 Planning", badge: todayInts.length },
    { id: "todos", lbl: "✅ Tâches", badge: activeTodos.length },
    { id: "vehicule", lbl: "🚗 Véhicule" },
    { id: "stats", lbl: "📊 Stats" },
  ];

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: T.muted }}>⏳ Chargement...</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* ── MODALE SIGNALEMENT ÉCHEC ────────────────────────── */}
      {echecModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(5,10,25,0.82)", backdropFilter: "blur(8px)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 20, width: "min(440px,96vw)", padding: "30px 26px", border: "2px solid #ef4444", boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>⛔</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: T.text, textAlign: "center", marginBottom: 6 }}>Signaler un échec</div>
            <div style={{ fontSize: 13, color: T.muted, textAlign: "center", marginBottom: 20, lineHeight: 1.5 }}>
              <strong style={{ color: T.text }}>{echecModal.titre}</strong><br />
              chez {echecModal.client_nom || "—"}
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, letterSpacing: "0.5px" }}>MOTIF DU BLOCAGE / NON-RÉSOLUTION</label>
            <textarea
              value={echecMotif}
              onChange={e => setEchecMotif(e.target.value)}
              placeholder="Ex: Pièce manquante, client absent, accès impossible, problème technique…"
              rows={3}
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 20 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setEchecModal(null); setEchecMotif(""); }}
                style={{ flex: 1, padding: "13px", borderRadius: 12, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={signalerEchec} disabled={echecSaving}
                style={{ flex: 2, padding: "13px", borderRadius: 12, background: "linear-gradient(135deg,#ef4444,#dc2626)", border: "none", color: "#fff", fontSize: 13, fontWeight: 900, cursor: echecSaving ? "not-allowed" : "pointer", opacity: echecSaving ? 0.7 : 1 }}>
                {echecSaving ? "⏳ Envoi…" : "⛔ Confirmer l'échec"}
              </button>
            </div>
            <div style={{ fontSize: 11, color: T.muted, textAlign: "center", marginTop: 12, lineHeight: 1.4 }}>
              Le ticket sera automatiquement placé en <strong style={{ color: "#f97316" }}>À re-planifier</strong> et la secrétaire sera notifiée.
            </div>
          </div>
        </div>
      )}

      {/* ── MONDAY KM LOCK MODAL ─────────────────────────────── */}
      {kmLocked && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(5,10,25,0.88)", backdropFilter: "blur(10px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card, borderRadius: 22, width: "min(460px,96vw)", padding: "36px 28px", border: `2px solid ${T.accent}`, boxShadow: "0 40px 100px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize: 48, textAlign: "center", marginBottom: 14 }}>📍</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.text, textAlign: "center", marginBottom: 8 }}>Relevé kilométrique du lundi</div>
            <div style={{ fontSize: 13, color: T.muted, textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
              Chaque lundi matin, vous devez saisir le kilométrage de votre véhicule avant de commencer votre journée. Cette action est obligatoire.
            </div>

            {vehicule && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 13, padding: "12px 16px", marginBottom: 22, display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ fontSize: 28 }}>🚗</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{vehicule.marque} {vehicule.modele}</div>
                  <div style={{ fontSize: 12, color: T.muted }}>{vehicule.immatriculation} · Dernier : {vehicule.dernier_km ? Number(vehicule.dernier_km).toLocaleString("fr-FR") + " km" : "—"}</div>
                </div>
              </div>
            )}

            <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8, letterSpacing: "0.5px" }}>KILOMÉTRAGE ACTUEL</label>
            <input
              type="number"
              value={kmValue}
              onChange={e => setKmValue(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitKm()}
              placeholder={vehicule?.dernier_km ? `Supérieur à ${Number(vehicule.dernier_km).toLocaleString("fr-FR")}` : "Ex: 45230"}
              style={{ width: "100%", padding: "16px", borderRadius: 13, border: `2px solid ${kmError ? "#ef4444" : T.border}`, background: T.bg, color: T.text, fontSize: 20, fontWeight: 800, outline: "none", boxSizing: "border-box", marginBottom: kmError ? 6 : 20 }}
              autoFocus
            />
            {kmError && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 16 }}>{kmError}</div>}

            <button onClick={submitKm} disabled={kmSaving}
              style={{ width: "100%", padding: "15px", borderRadius: 13, background: `linear-gradient(135deg,${T.accent},${T.purple || T.accent})`, border: "none", color: "#fff", fontSize: 15, fontWeight: 900, cursor: kmSaving ? "not-allowed" : "pointer", opacity: kmSaving ? 0.7 : 1 }}>
              {kmSaving ? "⏳ Enregistrement..." : "✅ Valider le relevé kilométrique"}
            </button>
          </div>
        </div>
      )}

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div style={{ background: `linear-gradient(135deg,${T.accent}20,${T.purple || T.accent}12)`, border: `1px solid ${T.accent}30`, borderRadius: 18, padding: "22px 26px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ fontSize: 23, fontWeight: 900, color: T.text }}>👋 Bonjour, {user.prenom} !</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 5 }}>
            {now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {todayInts.length} intervention(s) aujourd'hui
          </div>
          <div style={{ marginTop: 9, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: geoStatus === "tracking" ? "#10b981" : geoStatus === "denied" ? "#ef4444" : "#94a3b8", animation: geoStatus === "tracking" ? "geo-pulse 2s infinite" : undefined }} />
            <style>{`@keyframes geo-pulse{0%{box-shadow:0 0 0 0 rgba(16,185,129,0.4)}70%{box-shadow:0 0 0 6px rgba(16,185,129,0)}100%{box-shadow:0 0 0 0 rgba(16,185,129,0)}}`}</style>
            <span style={{ fontSize: 11, color: geoStatus === "tracking" ? "#10b981" : geoStatus === "denied" ? "#ef4444" : T.muted }}>
              {geoStatus === "tracking" ? "📍 Géolocalisation active" : geoStatus === "denied" ? "📍 Refusée" : "📍 Initialisation..."}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { val: thisMonthDone, lbl: "Ce mois", color: "#10b981" },
            { val: `${tauxPP}%`, lbl: "1er passage", color: T.accent },
            { val: `${cumulatedHours}h`, lbl: "Heures est.", color: T.warning || "#f59e0b" },
          ].map(s => (
            <div key={s.lbl} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 13, padding: "10px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 21, fontWeight: 900, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TABS ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 5, padding: 4, background: T.card, borderRadius: 14, border: `1px solid ${T.border}` }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: "9px 8px", borderRadius: 11, border: "none", background: tab === t.id ? T.accent : "transparent", color: tab === t.id ? "#fff" : T.muted, fontSize: 12, fontWeight: tab === t.id ? 700 : 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, transition: "all 0.15s", whiteSpace: "nowrap" }}>
            {t.lbl}
            {t.badge > 0 && (
              <span style={{ background: tab === t.id ? "rgba(255,255,255,0.25)" : T.accent, color: "#fff", borderRadius: 99, fontSize: 10, fontWeight: 800, padding: "1px 6px" }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── PLANNING TAB ─────────────────────────────────────── */}
      {tab === "planning" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Active intervention chrono */}
          {activeInt && (() => {
            void chronoSec;
            const elapsed = Math.floor((Date.now() - new Date(activeInt.started_at || Date.now()).getTime()) / 1000);
            const estSec = getEst(activeInt) * 60;
            const pct = Math.min(100, Math.round(elapsed / estSec * 100));
            const overtime = elapsed > estSec;
            const barColor = pct < 70 ? "#10b981" : pct < 90 ? "#f59e0b" : "#ef4444";
            return (
              <div style={{ background: T.card, border: `2px solid ${overtime ? "#ef4444" : T.accent}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "12px 18px", background: `${overtime ? "#ef4444" : T.accent}14`, borderBottom: `1px solid ${overtime ? "#ef4444" : T.accent}30`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 9, height: 9, borderRadius: "50%", background: overtime ? "#ef4444" : T.accent, animation: "geo-pulse 1.5s infinite" }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: overtime ? "#ef4444" : T.accent }}>{overtime ? "⚠️ DÉPASSEMENT" : "🔴 EN COURS"}</span>
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: overtime ? "#ef4444" : T.accent }}>{fmtChrono(elapsed)}</div>
                </div>
                <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{activeInt.titre}</div>
                  <div style={{ height: 8, background: T.border, borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 99, transition: "width 0.5s" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {activeInt.client_telephone && (
                      <a href={`tel:${activeInt.client_telephone}`} style={{ flex: 1, padding: "10px", borderRadius: 10, background: "#10b98118", border: "1px solid #10b98138", color: "#10b981", fontSize: 12, fontWeight: 700, textDecoration: "none", textAlign: "center", minWidth: 80 }}>📞 Appeler</a>
                    )}
                    {activeInt.map_lat && (
                      <a href={`https://waze.com/ul?ll=${activeInt.map_lat},${activeInt.map_lng}&navigate=yes`} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "10px", borderRadius: 10, background: "#33ccff18", border: "1px solid #33ccff38", color: "#33ccff", fontSize: 12, fontWeight: 700, textDecoration: "none", textAlign: "center", minWidth: 80 }}>🚗 Waze</a>
                    )}
                    <button onClick={() => onOpenPV && onOpenPV(activeInt)}
                      style={{ flex: 2, padding: "10px", borderRadius: 10, background: `linear-gradient(135deg,${T.accent},${T.purple || T.accent})`, border: "none", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", minWidth: 100 }}>
                      📋 Ouvrir le PV
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Today */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "13px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>📅 Aujourd'hui</div>
              <span style={{ fontSize: 12, color: T.muted }}>{todayInts.length} planifiée(s)</span>
            </div>
            {todayInts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 36, color: T.muted }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🎉</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Aucune intervention aujourd'hui</div>
              </div>
            ) : todayInts.map(i => {
              const sc = SC[i.statut] || T.muted;
              const isActive = ["en_cours", "in-progress"].includes(i.statut);
              return (
                <div key={i.id}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: `1px solid ${T.border}22`, background: isActive ? `${T.accent}08` : "transparent" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: T.accent, fontFamily: "monospace", flexShrink: 0, minWidth: 42 }}>
                    {fmtTime(i.date_planifiee)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.titre}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>👤 {i.client_nom || "—"} · 📍 {i.client_ville || "—"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: `${sc}22`, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase" }}>{SL[i.statut] || i.statut}</span>
                    {isActive && (
                      <button onClick={() => setEchecModal(i)}
                        style={{ padding: "7px 11px", borderRadius: 9, background: "#fff1f1", border: "1px solid #ef4444", color: "#ef4444", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                        title="Signaler un problème / intervention non résolue">
                        ⛔ Échec
                      </button>
                    )}
                    <button onClick={() => isActive ? (onOpenPV && onOpenPV(i)) : startIntervention(i)}
                      disabled={startingId === i.id}
                      style={{ padding: "7px 13px", borderRadius: 9, background: T.accent, border: "none", color: "#fff", fontSize: 11, fontWeight: 700, cursor: startingId === i.id ? "wait" : "pointer", opacity: startingId === i.id ? 0.7 : 1 }}>
                      {startingId === i.id ? "⏳" : isActive ? "📋 PV" : "▶ Démarrer"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Upcoming */}
          {upcomingInts.length > 0 && (
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>📆 À venir</div>
              </div>
              {upcomingInts.map(i => (
                <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${T.border}22` }}>
                  <div style={{ fontSize: 11, color: T.muted, flexShrink: 0, minWidth: 80 }}>{fmtDay(i.date_planifiee)}</div>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.titre}</div>
                  <div style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>{i.client_ville || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TODOS TAB ────────────────────────────────────────── */}
      {tab === "todos" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              value={newTodoText}
              onChange={e => setNewTodoText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addTodo()}
              placeholder="Nouvelle tâche…"
              style={{ flex: 1, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border}`, background: T.card, color: T.text, fontSize: 13, outline: "none" }}
            />
            <button onClick={addTodo}
              style={{ padding: "12px 20px", borderRadius: 12, background: T.accent, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              + Ajouter
            </button>
          </div>

          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Mes tâches actives</div>
              <span style={{ fontSize: 12, color: T.muted }}>{activeTodos.length} tâche(s)</span>
            </div>
            {activeTodos.length === 0 ? (
              <div style={{ textAlign: "center", padding: 36, color: T.muted }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Tout est à jour !</div>
              </div>
            ) : activeTodos.map(t => {
              const isUrgent = ["urgent", "high"].includes(t.priorite);
              return (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: `1px solid ${T.border}22` }}>
                  <button onClick={() => completeTodo(t.id)}
                    style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${T.accent}`, background: "transparent", cursor: "pointer", flexShrink: 0, transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.titre}</div>
                    {t.deadline && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>📅 {new Date(t.deadline).toLocaleDateString("fr-FR")}</div>}
                  </div>
                  {isUrgent && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", background: "#ef444422", padding: "2px 8px", borderRadius: 20 }}>
                      {t.priorite === "urgent" ? "🔴 Urgent" : "🟠 Haute"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── VEHICULE TAB ─────────────────────────────────────── */}
      {tab === "vehicule" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {!vehicule ? (
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: 36, textAlign: "center", color: T.muted }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🚗</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Aucun véhicule assigné</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Contactez votre responsable</div>
            </div>
          ) : (
            <>
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", background: `linear-gradient(135deg,${T.accent}14,${T.bg})`, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 40 }}>🚗</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{vehicule.marque} {vehicule.modele}</div>
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{vehicule.immatriculation} · {vehicule.carburant} · {vehicule.annee || "—"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: T.accent }}>{Number(vehicule.dernier_km || vehicule.kilometrage_actuel || 0).toLocaleString("fr-FR")} km</div>
                    <div style={{ fontSize: 10, color: T.muted }}>Dernier relevé</div>
                  </div>
                </div>

                <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { lbl: "Contrôle technique", val: fmtFull(vehicule.date_controle_technique), date: vehicule.date_controle_technique },
                    { lbl: "Assurance", val: fmtFull(vehicule.date_assurance), date: vehicule.date_assurance },
                  ].map(({ lbl, val, date }) => {
                    const expired = isExpired(date);
                    const soon = !expired && isExpiringSoon(date);
                    const bColor = expired ? "#ef4444" : soon ? "#f59e0b" : T.border;
                    const tColor = expired ? "#ef4444" : soon ? "#f59e0b" : T.text;
                    return (
                      <div key={lbl} style={{ background: T.bg, borderRadius: 11, padding: "11px 14px", border: `1px solid ${bColor}` }}>
                        <div style={{ fontSize: 10, color: T.muted, marginBottom: 4 }}>{lbl}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: tColor }}>{val}</div>
                        {expired && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 3 }}>⚠️ Expiré</div>}
                        {soon && <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 3 }}>⚠️ Expire bientôt</div>}
                      </div>
                    );
                  })}
                </div>

                {vehicule.notes && (
                  <div style={{ padding: "0 20px 16px" }}>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: 12, color: T.text }}>{vehicule.notes}</div>
                  </div>
                )}
              </div>

              {/* Km input */}
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "16px 20px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>📍 Saisir un relevé kilométrique</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <input
                    type="number"
                    value={kmValue}
                    onChange={e => setKmValue(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submitKm()}
                    placeholder={`Ex: ${Number(vehicule.dernier_km || vehicule.kilometrage_actuel || 0) + 100}`}
                    style={{ flex: 1, padding: "11px 14px", borderRadius: 11, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 14, outline: "none" }}
                  />
                  <button onClick={submitKm} disabled={kmSaving}
                    style={{ padding: "11px 20px", borderRadius: 11, background: T.accent, border: "none", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {kmSaving ? "..." : "Valider"}
                  </button>
                </div>
                {kmError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 6 }}>{kmError}</div>}
              </div>
            </>
          )}

          {/* Stock tampon */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
            <div style={{ padding: "13px 18px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>📦 Stock tampon</div>
              <span style={{ fontSize: 12, color: T.muted }}>{stockTampon.length} article(s)</span>
            </div>
            {stockTampon.length === 0 ? (
              <div style={{ textAlign: "center", padding: 28, color: T.muted, fontSize: 13 }}>Aucun article dans votre stock tampon</div>
            ) : stockTampon.map(s => {
              const isZero = s.quantite === 0;
              const isCrit = s.quantite > 0 && s.quantite <= 2;
              return (
                <div key={s.id}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${T.border}22`, background: isZero ? "#ef444408" : "transparent" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.designation}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{s.categorie || "—"} · {s.prod_ref || "—"}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 17, fontWeight: 900, color: isZero ? "#ef4444" : isCrit ? "#f59e0b" : "#10b981" }}>{s.quantite}</span>
                    {isZero && <span style={{ fontSize: 9, fontWeight: 700, color: "#ef4444", background: "#ef444422", padding: "2px 6px", borderRadius: 20 }}>RUPTURE</span>}
                    {isCrit && <span style={{ fontSize: 9, fontWeight: 700, color: "#f59e0b", background: "#f59e0b22", padding: "2px 6px", borderRadius: 20 }}>CRITIQUE</span>}
                    <button onClick={() => { setUseStockItem(s); setUseQty(1); }} disabled={isZero}
                      style={{ padding: "6px 11px", borderRadius: 8, background: isZero ? "transparent" : T.accentDim, border: `1px solid ${isZero ? T.border : T.accent}40`, color: isZero ? T.muted : T.accent, fontSize: 11, fontWeight: 600, cursor: isZero ? "not-allowed" : "pointer" }}>
                      Utiliser
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STATS TAB ────────────────────────────────────────── */}
      {tab === "stats" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
            {[
              { ic: "✅", val: totalDone, lbl: "Total terminées", color: "#10b981" },
              { ic: "📅", val: thisMonthDone, lbl: "Ce mois-ci", color: T.accent },
              { ic: "🎯", val: `${tauxPP}%`, lbl: "Taux résolution", color: T.warning || "#f59e0b" },
              { ic: "⏱️", val: `${cumulatedHours}h`, lbl: "Heures estimées", color: T.purple || T.accent },
            ].map(s => (
              <div key={s.lbl} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "22px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 30, marginBottom: 8 }}>{s.ic}</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: s.color, letterSpacing: "-1px" }}>{s.val}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          {/* By status */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "16px 18px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 14 }}>Suivi par statut</div>
            {[
              { lbl: "Terminées", count: totalDone, color: "#10b981" },
              { lbl: "En cours", count: interventions.filter(i => ["en_cours", "in-progress"].includes(i.statut)).length, color: T.accent },
              { lbl: "Planifiées", count: interventions.filter(i => ["planifiee", "pending"].includes(i.statut)).length, color: T.warning || "#f59e0b" },
              { lbl: "Annulées", count: interventions.filter(i => i.statut === "annulee").length, color: "#ef4444" },
            ].map(({ lbl, count, color }) => (
              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: T.text, width: 90 }}>{lbl}</div>
                <div style={{ flex: 1, height: 7, background: T.border, borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: interventions.length > 0 ? `${count / interventions.length * 100}%` : "0%", background: color, borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color, minWidth: 24, textAlign: "right" }}>{count}</div>
              </div>
            ))}
          </div>

          {/* By type */}
          {(() => {
            const byType = {};
            interventions.forEach(i => { const t = i.type_intervention || "Autre"; byType[t] = (byType[t] || 0) + 1; });
            const entries = Object.entries(byType).sort(([, a], [, b]) => b - a);
            if (entries.length === 0) return null;
            return (
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "16px 18px" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 14 }}>Répartition par type</div>
                {entries.map(([type, count]) => (
                  <div key={type} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: T.text, width: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{type}</div>
                    <div style={{ flex: 1, height: 7, background: T.border, borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: interventions.length > 0 ? `${count / interventions.length * 100}%` : "0%", background: T.accent, borderRadius: 99 }} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.muted, minWidth: 24, textAlign: "right" }}>{count}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── USE STOCK MODAL ──────────────────────────────────── */}
      {useStockItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(8,12,30,0.5)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setUseStockItem(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: T.card, borderRadius: 18, width: "min(380px,96vw)", padding: "26px 22px", border: `1px solid ${T.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 4 }}>📦 Utiliser du stock</div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 22 }}>{useStockItem.designation}</div>
            <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 8 }}>QUANTITÉ (disponible : {useStockItem.quantite})</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20 }}>
              <button onClick={() => setUseQty(q => Math.max(1, q - 1))}
                style={{ width: 38, height: 38, borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 20, cursor: "pointer" }}>−</button>
              <input type="number" value={useQty}
                onChange={e => setUseQty(Math.max(1, Math.min(useStockItem.quantite, parseInt(e.target.value) || 1)))}
                style={{ flex: 1, padding: "9px", borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 17, fontWeight: 800, textAlign: "center", outline: "none" }} />
              <button onClick={() => setUseQty(q => Math.min(useStockItem.quantite, q + 1))}
                style={{ width: 38, height: 38, borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 20, cursor: "pointer" }}>+</button>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setUseStockItem(null)}
                style={{ flex: 1, padding: "12px", borderRadius: 11, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Annuler</button>
              <button onClick={useStock}
                style={{ flex: 2, padding: "12px", borderRadius: 11, background: T.accent, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✅ Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
